import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Spinner } from 'react-bootstrap';
import ReactSelect from 'react-select';
import { schema } from './Validate';
import ErrorMessage from '../../../../ErrorMessage/ErrorMessage';
import StepNavigation from '../../components/StepNavigation/StepNavigation';
import FinanceirasConsentModal from '../../components/FinanceirasConsentModal/FinanceirasConsentModal';
import { useSimulationBank } from '../../../../../providers/SimulationBank/SimulationBankProvider';
import { LocationsService } from '../../../../../services/SimulationBank/LocationsService';
import { SimulationBankService } from '../../../../../services/SimulationBank/SimulationBankService';
import { ClienteScreening, FormDataCLT, StateOption, CityOption } from '../../../../../models/SimulationBank/SimulationBankModels';
import { applyMaskCPF, applyMaskPhone } from '../utils/masks';
import { cpfValidator } from '../utils/validators';
import { extractClienteScreening, matchCityOption, matchStateOption, matchesCpf } from './clientLookup';
import { iniciarGeolocalizacao, manterGeolocalizacaoAtualizada } from '../../../../../utils/deviceEvidence';

interface Step1FormProps {
    onAdvance: () => void;
    onBack: () => void;
}

// ─── Cache ────────────────────────────────────────────────────────────────────
const CACHE_KEY = '@beviajuda:sim-step1-clt';
const CACHE_TTL = 60 * 60 * 1000;

type CachedStep1 = {
    formValues: Partial<FormDataCLT>;
    selects: {
        state: StateOption | null;
        city: CityOption | null;
        sexo: any;
        alfa: any;
        mob: any;
        legado: any;
        tipoProduto: any;
    };
    savedAt: number;
    // SB-1953 — cache escrito pela ação "Simular" do histórico de simulações, e não pelo
    // uso normal do formulário (ver handleConfirmarPrefill em SimulationBank.tsx).
    prefilledFromHistorico?: boolean;
};

const saveCache = (formValues: Partial<FormDataCLT>, selects: CachedStep1['selects']) => {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ formValues, selects, savedAt: Date.now() }));
    } catch {}
};

const loadCache = (): CachedStep1 | null => {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const cache: CachedStep1 = JSON.parse(raw);
        if (Date.now() - cache.savedAt > CACHE_TTL) { localStorage.removeItem(CACHE_KEY); return null; }
        return cache;
    } catch { return null; }
};

const clearCache = () => { try { localStorage.removeItem(CACHE_KEY); } catch {} };

// ─── Autopreenchimento por CPF (AI-1204) ──────────────────────────────────────
// Campos alimentados pela consulta cadastral. O CPF fica de fora de propósito: é a
// chave digitada pelo parceiro e nunca é reescrito pela resposta (CA10).
type AutoFillKey = 'nome_completo' | 'data_nascimento' | 'telefone' | 'estado' | 'cidade';

const AUTO_FILL_KEYS: AutoFillKey[] = ['nome_completo', 'data_nascimento', 'estado', 'cidade'];

const mapCities = (response: any): CityOption[] =>
    (response?.data ?? response ?? []).map((c: any) => ({
        value: c.nome ?? c.name ?? c.id,
        label: c.nome ?? c.name,
    }));

// ─── Styles & helpers ─────────────────────────────────────────────────────────
const selectStyles = {
    control: (base: any, state: any) => ({
        ...base,
        minHeight: '44px',
        background: state.isFocused ? '#e8e9ef' : '#f0f1f5',
        border: `1.5px solid ${state.isFocused ? '#c5c9d3' : '#e2e5ea'}`,
        borderRadius: '10px',
        fontSize: '14px',
        boxShadow: 'none',
        outline: 'none',
        '&:hover': { borderColor: '#c5c9d3', boxShadow: 'none' },
    }),
    valueContainer: (base: any) => ({ ...base, padding: '0 16px' }),
    option: (base: any, state: any) => ({
        ...base,
        fontSize: '14px',
        backgroundColor: state.isSelected ? '#19c9dd' : state.isFocused ? '#f0fdff' : '#fff',
        color: state.isSelected ? '#171a3d' : '#101426',
    }),
    placeholder: (base: any) => ({ ...base, color: '#667085', fontSize: '14px' }),
    indicatorSeparator: () => ({ display: 'none' }),
    menu: (base: any) => ({ ...base, zIndex: 9999 }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

const Step1Form: FC<Step1FormProps> = ({ onAdvance, onBack }) => {
    const { formData, handleTriagem, isLoadingTriagem, setFormData } = useSimulationBank();
    const [showModal, setShowModal] = useState(false);
    const [showOptional, setShowOptional] = useState(false);
    const [pendingData, setPendingData] = useState<FormDataCLT | null>(null);
    const [states, setStates] = useState<StateOption[]>([]);
    const [cities, setCities] = useState<CityOption[]>([]);
    const [loadingCities, setLoadingCities] = useState(false);
    const [selectedState, setSelectedState] = useState<StateOption | null>(null);
    const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
    const [selectedSexo, setSelectedSexo] = useState<any>(null);
    const [selectedAlfa, setSelectedAlfa] = useState<any>(null);
    const [selectedMob, setSelectedMob] = useState<any>(null);
    const [selectedLegado, setSelectedLegado] = useState<any>(null);
    const [selectedTipoProduto, setSelectedTipoProduto] = useState<any>(null);
    const [isConsultingCpf, setIsConsultingCpf] = useState(false);
    const locationsService = new LocationsService();

    // Ref para os selects atuais — usado dentro da subscription do watch
    const selectsRef = useRef<CachedStep1['selects']>({
        state: null, city: null, sexo: null, alfa: null, mob: null, legado: null, tipoProduto: null,
    });

    // Campos preenchidos pela consulta e ainda não mexidos pelo parceiro: podem ser
    // sobrescritos por uma consulta nova (CA9), os demais não (CA4).
    const autoFilledRef = useRef<Set<AutoFillKey>>(new Set());
    // Campos que o parceiro editou — inclusive para apagar. Uma vez tocado, o campo é
    // dele e a consulta não encosta mais nele (CA4).
    const partnerTouchedRef = useRef<Set<AutoFillKey>>(new Set());
    // Sequência da consulta em voo: descarta retorno de um CPF que não é mais o atual.
    const consultSeqRef = useRef(0);
    const lastConsultedCpfRef = useRef('');
    // Promise do carregamento de estados: o retorno da consulta pode chegar antes da
    // lista, e sem ela não há como resolver a opção de Estado (CA7).
    const statesPromiseRef = useRef<Promise<StateOption[]> | null>(null);

    const { handleSubmit, register, setValue, watch, reset, trigger, getValues, formState: { errors } } = useForm<FormDataCLT>({
        mode: 'onChange',
        resolver: yupResolver(schema) as any,
    });

    const admissaoError = errors.data_admissao?.message ?? '';

    // ─── Persistência automática: salva no cache a cada mudança ─────────────────
    useEffect(() => {
        const subscription = watch((values) => {
            saveCache(values as Partial<FormDataCLT>, selectsRef.current);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    const loadStates = async (): Promise<StateOption[]> => {
        const [response, error] = await new LocationsService().getStates();
        if (error) return [];
        const loadedStates: StateOption[] = (response?.data ?? response ?? []).map((s: any) => ({
            value: s.uf ?? s.sigla ?? s.id,
            label: s.nome ?? s.name ?? s.uf,
            uf: s.uf ?? s.sigla,
        }));
        setStates(loadedStates);
        return loadedStates;
    };

    // ─── Mount: adianta a localização (AI-1277) ──────────────────────────────────
    // Não abre prompt (a permissão é pedida na home): só usa o navegador se já estiver
    // permitido, senão o fallback do Google. A posição fica pronta para o clique e é
    // renovada enquanto a tela está aberta.
    useEffect(() => {
        iniciarGeolocalizacao();
        return manterGeolocalizacaoAtualizada();
    }, []);

    // ─── Mount: carrega estados e restaura cache ─────────────────────────────────
    useEffect(() => {
        const init = async () => {
            statesPromiseRef.current = loadStates();
            const loadedStates = await statesPromiseRef.current;
            if (!loadedStates.length) return;

            const cached = loadCache();
            if (cached) {
                // SB-1953 — quando a jornada vem do histórico de simulações já com o
                // cadastro completo, a consulta por CPF não teria o que acrescentar: damos
                // ela por feita para este CPF (é a mesma guarda que evita reconsultar o
                // mesmo documento) e marcamos os campos como autopreenchidos, para que
                // trocar o CPF os limpe como dado de outra pessoa (CA9).
                // A condição exige os campos de fato preenchidos, então enquanto o
                // histórico devolver só o CPF a consulta continua rodando normalmente.
                const cachedCpf = String(cached.formValues.cpf ?? '').replace(/\D/g, '');
                const temCadastroCompleto = AUTO_FILL_KEYS.every(
                    (key) => !!String(cached.formValues[key] ?? '').trim()
                );
                if (cached.prefilledFromHistorico && temCadastroCompleto && cachedCpf) {
                    lastConsultedCpfRef.current = cachedCpf;
                    AUTO_FILL_KEYS.forEach((key) => autoFilledRef.current.add(key));
                }

                reset(cached.formValues as FormDataCLT);
                setSelectedSexo(cached.selects.sexo);
                setSelectedAlfa(cached.selects.alfa);
                setSelectedMob(cached.selects.mob);
                setSelectedLegado(cached.selects.legado);
                setSelectedTipoProduto(cached.selects.tipoProduto);
                selectsRef.current = cached.selects;

                if (cached.selects.state) {
                    setSelectedState(cached.selects.state);
                    // Carrega cidades do estado cacheado
                    setLoadingCities(true);
                    const [cityRes] = await new LocationsService().getCities(cached.selects.state.uf);
                    setLoadingCities(false);
                    setCities(mapCities(cityRes));
                    if (cached.selects.city) setSelectedCity(cached.selects.city);
                }
                return;
            }

            if (formData) reset(formData);
        };

        init();
    }, []);

    const handleStateChange = async (opt: StateOption | null) => {
        syncPartnerTouch('estado', !!opt?.value);
        // Cidade é sempre limpa junto (abaixo) — fica sem valor e, portanto, elegível de
        // novo para autopreenchimento, igual a qualquer outro campo esvaziado.
        syncPartnerTouch('cidade', false);
        setSelectedState(opt);
        setValue('estado', opt?.value ?? '', { shouldValidate: true });
        setValue('cidade', '', { shouldValidate: true });
        setSelectedCity(null);
        setCities([]);
        const next = { ...selectsRef.current, state: opt, city: null };
        selectsRef.current = next;
        saveCache(watch(), next);

        if (!opt?.uf) return;
        setLoadingCities(true);
        const [response, error] = await locationsService.getCities(opt.uf);
        setLoadingCities(false);
        if (error) return;
        setCities(mapCities(response));
    };

    const updateSelect = (key: keyof CachedStep1['selects'], value: any) => {
        const next = { ...selectsRef.current, [key]: value };
        selectsRef.current = next;
        saveCache(watch(), next);
    };

    // ─── Autopreenchimento a partir do CPF (AI-1204) ──────────────────────────
    // O enriquecimento é conveniência, não dependência: nada aqui bloqueia o formulário
    // e nenhuma falha vira mensagem de erro na tela (CA2, CA8).

    // "Tocado" é sobre ter conteúdo digitado pelo parceiro, não sobre ter sido mexido em
    // algum momento: se ele apaga o campo e deixa vazio, não há mais valor dele a
    // proteger, então o campo volta a ficar elegível para autopreenchimento (CA4).
    const syncPartnerTouch = (key: AutoFillKey, hasValue: boolean) => {
        if (hasValue) partnerTouchedRef.current.add(key);
        else partnerTouchedRef.current.delete(key);
        autoFilledRef.current.delete(key);
    };

    // CA4 — só entra em campo vazio e nunca tocado pelo parceiro. Campo que já veio da
    // consulta e continua intocado pode ser refeito por uma consulta nova (CA9).
    const canAutoFill = (key: AutoFillKey, values: Partial<FormDataCLT>): boolean => {
        if (partnerTouchedRef.current.has(key)) return false;
        const current = values[key];
        return !String(current ?? '').trim() || autoFilledRef.current.has(key);
    };

    // CA9 — trocado o CPF, o que veio da consulta anterior é dado de outra pessoa e sai
    // de cena. O que o parceiro digitou permanece.
    const clearStaleAutoFill = () => {
        const filled = autoFilledRef.current;
        if (!filled.size) return;

        if (filled.has('nome_completo')) setValue('nome_completo', '', { shouldValidate: false });
        if (filled.has('data_nascimento')) setValue('data_nascimento', '', { shouldValidate: false });
        if (filled.has('telefone')) setValue('telefone', '', { shouldValidate: false });
        if (filled.has('estado') || filled.has('cidade')) {
            setSelectedState(null);
            setSelectedCity(null);
            setCities([]);
            setValue('estado', '', { shouldValidate: false });
            setValue('cidade', '', { shouldValidate: false });
            const next = { ...selectsRef.current, state: null, city: null };
            selectsRef.current = next;
            saveCache(getValues(), next);
        }
        autoFilledRef.current = new Set();
    };

    // CA7 — Estado e Cidade são preenchidos juntos ou nenhum dos dois: o Estado entra
    // primeiro, a lista de cidades do UF é carregada e a cidade retornada já vai
    // selecionada. Qualquer etapa que não case aborta o par inteiro.
    const applyLocationAutoFill = async (cliente: ClienteScreening, seq: number) => {
        if (!cliente.estado || !cliente.cidade) return;

        const values = getValues();
        if (!canAutoFill('estado', values) || !canAutoFill('cidade', values)) return;

        const loadedStates = (await statesPromiseRef.current) ?? states;
        const stateOption = matchStateOption(loadedStates, cliente.estado);
        if (!stateOption?.uf) return;

        const [cityResponse, cityError] = await locationsService.getCities(stateOption.uf);
        if (cityError) return;

        const loadedCities = mapCities(cityResponse);
        const cityOption = matchCityOption(loadedCities, cliente.cidade);
        if (!cityOption) return;

        // Reconfere depois dos awaits: o parceiro pode ter escolhido o estado ou trocado
        // o CPF enquanto as cidades carregavam.
        if (seq !== consultSeqRef.current) return;
        const currentValues = getValues();
        if (!canAutoFill('estado', currentValues) || !canAutoFill('cidade', currentValues)) return;

        setSelectedState(stateOption);
        setCities(loadedCities);
        setSelectedCity(cityOption);
        setValue('estado', stateOption.value, { shouldValidate: true });
        setValue('cidade', cityOption.value, { shouldValidate: true });
        autoFilledRef.current.add('estado');
        autoFilledRef.current.add('cidade');

        const next = { ...selectsRef.current, state: stateOption, city: cityOption };
        selectsRef.current = next;
        saveCache(getValues(), next);
    };

    // CA3/CA5 — preenche os campos disponíveis; todos seguem habilitados e editáveis.
    const applyAutoFill = async (cliente: ClienteScreening, seq: number) => {
        const values = getValues();

        if (cliente.nome_completo && canAutoFill('nome_completo', values)) {
            setValue('nome_completo', cliente.nome_completo, { shouldValidate: true });
            autoFilledRef.current.add('nome_completo');
        }
        if (cliente.data_nascimento && canAutoFill('data_nascimento', values)) {
            setValue('data_nascimento', cliente.data_nascimento, { shouldValidate: true });
            autoFilledRef.current.add('data_nascimento');
        }
        if (cliente.telefone_celular && canAutoFill('telefone', values)) {
            setValue('telefone', applyMaskPhone(cliente.telefone_celular), { shouldValidate: true });
            autoFilledRef.current.add('telefone');
        }

        await applyLocationAutoFill(cliente, seq);
    };

    const consultCpf = async (digits: string, seq: number) => {
        setIsConsultingCpf(true);
        const [response, error] = await new SimulationBankService().getClienteByCpf(digits);
        if (seq === consultSeqRef.current) setIsConsultingCpf(false);

        // CA8 — falha, timeout ou ausência de dados não geram mensagem: os campos
        // simplesmente ficam como estão.
        if (error || seq !== consultSeqRef.current) return;

        const cliente = extractClienteScreening(response);
        if (!cliente) return;

        // CA6 — conferência de chave: resposta de outro CPF é descartada por inteiro.
        if (!matchesCpf(cliente.cpf, digits)) {
            console.warn('[Step1Form] Consulta por CPF retornou documento divergente — resposta descartada.');
            return;
        }

        await applyAutoFill(cliente, seq);
    };

    // CA1/CA9 — CPF válido dispara a consulta em segundo plano, sem ação do parceiro, e
    // qualquer troca de CPF dispara uma nova.
    const cpfValue = watch('cpf');
    useEffect(() => {
        const digits = (cpfValue ?? '').replace(/\D/g, '');
        if (!cpfValidator(digits)) {
            // CPF incompleto/apagado: zera a memória do último consultado. Sem isso, apagar
            // e redigitar o mesmo CPF ficaria travado achando que já foi consultado.
            lastConsultedCpfRef.current = '';
            return;
        }
        if (digits === lastConsultedCpfRef.current) return;

        lastConsultedCpfRef.current = digits;
        const seq = ++consultSeqRef.current;
        clearStaleAutoFill();
        consultCpf(digits, seq);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cpfValue]);

    const onSubmit = async (data: FormDataCLT) => {
        setFormData(data);
        setPendingData(data);
        setShowModal(true);
    };

    const handleAccept = async (financeiras: string[]) => {
        if (!pendingData) return;
        const ok = await handleTriagem({ ...pendingData, financeiras });
        if (ok) { clearCache(); setShowModal(false); onAdvance(); }
    };

    const yesNoOptions = [{ value: 'true', label: 'Sim' }, { value: 'false', label: 'Não' }];
    const sexoOptions = [{ value: 'M', label: 'Masculino' }, { value: 'F', label: 'Feminino' }, { value: 'O', label: 'Outro' }];
    const tipoProdutoOptions = [
        { value: 'NOVO', label: 'Novo' },
        { value: 'REFIN DE CARTEIRA', label: 'Refin de Carteira' },
        { value: 'PORT COM REFIN DA PORT', label: 'Port com Refin da Port' },
    ];

    return (
        <>
        <FinanceirasConsentModal
            show={showModal}
            isSubmitting={isLoadingTriagem}
            onBack={() => setShowModal(false)}
            onAccept={handleAccept}
        />
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="sim-form">
            <div className="fsec-header">
                <div className="fsec-title">Dados do cliente para simulação</div>
                <span className="fsec-title">(OBRIGATÓRIOS)</span>
            </div>

            {/* CPF em primeiro: é a chave da consulta cadastral que preenche o restante (AI-1204). */}
            <div className="frow">
                <div className="fgroup">
                    <label>CPF <span className="req">*</span></label>
                    <input type="text" className={errors.cpf ? 'invalid' : ''} placeholder="000.000.000-00" maxLength={14} {...register('cpf')} onChange={(e) => setValue('cpf', applyMaskCPF(e.target.value), { shouldValidate: true })} />
                    <ErrorMessage name="CPF" type={errors.cpf?.type} />
                    {isConsultingCpf && (
                        <span className="cpf-lookup-hint">
                            <Spinner animation="border" size="sm" /> Buscando dados do cliente...
                        </span>
                    )}
                </div>
                <div className="fgroup">
                    <label>Nome completo <span className="req">*</span></label>
                    <input type="text" className={errors.nome_completo ? 'invalid' : ''} placeholder="Digite o nome completo" {...register('nome_completo', { onChange: (e) => syncPartnerTouch('nome_completo', !!e.target.value?.trim()) })} />
                    <ErrorMessage name="Nome completo" type={errors.nome_completo?.type} />
                </div>
            </div>

            <div className="frow">
                <div className="fgroup">
                    <label>Data de Nascimento <span className="req">*</span></label>
                    <input type="date" className={errors.data_nascimento ? 'invalid' : ''} {...register('data_nascimento', { onChange: (e) => syncPartnerTouch('data_nascimento', !!e.target.value) })} />
                    <ErrorMessage name="Data de Nascimento" type={errors.data_nascimento?.type} />
                </div>
                <div className="fgroup">
                    <label>Telefone Celular <span className="req">*</span></label>
                    <input type="text" className={errors.telefone ? 'invalid' : ''} placeholder="(00) 00000-0000" maxLength={15} {...register('telefone')} onChange={(e) => { syncPartnerTouch('telefone', !!e.target.value?.trim()); setValue('telefone', applyMaskPhone(e.target.value), { shouldValidate: true }); }} />
                    <ErrorMessage name="Telefone" type={errors.telefone?.type} />
                </div>
            </div>

            <div className="frow">
                <div className="fgroup">
                    <label>Estado <span className="req">*</span></label>
                    <input type="hidden" {...register('estado')} />
                    <ReactSelect options={states} value={selectedState} styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" placeholder="Selecione..." noOptionsMessage={() => 'Sem opções'} onChange={(v: any) => handleStateChange(v)} className={errors.estado ? 'invalid' : ''} />
                    <ErrorMessage name="Estado" type={errors.estado?.type} />
                </div>
                <div className="fgroup">
                    <label>Cidade <span className="req">*</span></label>
                    <input type="hidden" {...register('cidade')} />
                    <ReactSelect options={cities} value={selectedCity} styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" isLoading={loadingCities} isDisabled={!selectedState} placeholder={selectedState ? 'Selecione a cidade...' : 'Selecione o estado primeiro'} noOptionsMessage={() => 'Sem cidades'} onChange={(v: any) => { syncPartnerTouch('cidade', !!v?.value); setSelectedCity(v); setValue('cidade', v?.value ?? '', { shouldValidate: true }); updateSelect('city', v); }} className={errors.cidade ? 'invalid' : ''} />
                    <ErrorMessage name="Cidade" type={errors.cidade?.type} />
                </div>
            </div>

            <div className={`fin-category step1-fin-category ${showOptional ? 'open' : ''}`} style={{ marginTop: 4 }}>
                <div className="fin-category-header step1-fin-category-header" onClick={() => setShowOptional((v) => !v)}>
                    <div className="fin-category-header__left">
                        <span className="fin-category-title step1-fin-category-title">Complementar dados para que o BevIAjuda faça uma análisa de probabilidade de aprovação dos bancos parceiros considerando roteiro operacional</span>
                    </div>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} className="fin-category-arrow step1-fin-category-arrow">
                        <path d="M6 9l6 6 6-6" />
                    </svg>
                </div>
                {showOptional && (
                <div className="fin-category-body step1-fin-category-body">

            <div className="frow">
                <div className="fgroup">
                    <label>Alfabetizado?</label>
                    <input type="hidden" {...register('alfabetizado')} />
                    <ReactSelect options={yesNoOptions} value={selectedAlfa} styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" placeholder="Selecione..." noOptionsMessage={() => 'Sem opções'} onChange={(v: any) => { setSelectedAlfa(v); setValue('alfabetizado', v?.value, { shouldValidate: true }); updateSelect('alfa', v); }} className={errors.alfabetizado ? 'invalid' : ''} />
                    <ErrorMessage name="Alfabetizado" type={errors.alfabetizado?.type} />
                </div>
                <div className="fgroup">
                    <label>Mobilidade Reduzida?</label>
                    <input type="hidden" {...register('mobilidade_reduzida')} />
                    <ReactSelect options={yesNoOptions} value={selectedMob} styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" placeholder="Selecione..." noOptionsMessage={() => 'Sem opções'} onChange={(v: any) => { setSelectedMob(v); setValue('mobilidade_reduzida', v?.value, { shouldValidate: true }); updateSelect('mob', v); }} className={errors.mobilidade_reduzida ? 'invalid' : ''} />
                    <ErrorMessage name="Mobilidade Reduzida" type={errors.mobilidade_reduzida?.type} />
                </div>
            </div>

            <div className="frow">
                <div className="fgroup">
                    <label>Sexo</label>
                    <input type="hidden" {...register('sexo')} />
                    <ReactSelect options={sexoOptions} value={selectedSexo} styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" placeholder="Selecione..." noOptionsMessage={() => 'Sem opções'} onChange={(v: any) => { setSelectedSexo(v); setValue('sexo', v?.value, { shouldValidate: true }); updateSelect('sexo', v); }} className={errors.sexo ? 'invalid' : ''} />
                    <ErrorMessage name="Sexo" type={errors.sexo?.type} />
                </div>
                <div className="fgroup">
                    <label>Nacionalidade</label>
                    <input type="text" className={errors.nacionalidade ? 'invalid' : ''} placeholder="Ex: Brasileira" {...register('nacionalidade')} />
                    <ErrorMessage name="Nacionalidade" type={errors.nacionalidade?.type} />
                </div>
            </div>

            <div className="admissao-block">
                <div className="admissao-row">
                    <div className="fgroup" style={{ marginBottom: 0 }}>
                        <label>Data de Admissão</label>
                        <input type="date" className={admissaoError ? 'invalid' : ''} {...register('data_admissao', { onChange: () => trigger('data_admissao') })} />
                    </div>
                    <div className="admissao-ou">OU</div>
                    <div className="fgroup" style={{ marginBottom: 0 }}>
                        <label>Vínculo (Meses)</label>
                        <input type="number" min={0} className={admissaoError ? 'invalid' : ''} placeholder="Ex: 48" {...register('tempo_admissao', { onChange: () => trigger('data_admissao') })} />
                    </div>
                </div>
                <p className={`admissao-hint${admissaoError ? ' error' : ''}`}>* Preencha apenas uma das opções de tempo de vínculo.</p>
            </div>

            <div className="fgroup">
                <label>Possui Empréstimo Consignado Legado?</label>
                <input type="hidden" {...register('legado')} />
                <ReactSelect options={yesNoOptions} value={selectedLegado} styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" placeholder="Selecione..." noOptionsMessage={() => 'Sem opções'} onChange={(v: any) => { setSelectedLegado(v); setValue('legado', v?.value, { shouldValidate: true }); updateSelect('legado', v); }} className={errors.legado ? 'invalid' : ''} />
                <ErrorMessage name="Empréstimo Legado" type={errors.legado?.type} />
            </div>

            <div className="frow">
                <div className="fgroup">
                    <label>Data de Início do CNPJ</label>
                    <input type="date" {...register('data_inicio_cnpj')} />
                </div>
                <div className="fgroup">
                    <label>Quantidade de Funcionários</label>
                    <input type="number" min={0} placeholder="Ex: 15" {...register('quantidade_funcionarios')} />
                </div>
            </div>

            <div className="frow">
                <div className="fgroup">
                    <label>Tipo de Produto</label>
                    <ReactSelect options={tipoProdutoOptions} value={selectedTipoProduto} styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" placeholder="Selecione..." noOptionsMessage={() => 'Sem opções'} isClearable onChange={(v: any) => { setSelectedTipoProduto(v); setValue('tipo_produto', v?.value ?? ''); updateSelect('tipoProduto', v); }} />
                </div>
                <div className="fgroup">
                    <label>Parcela Mínima (R$)</label>
                    <input type="number" min={0} step="0.01" placeholder="Ex: 200.00" {...register('parcela_minima')} />
                </div>
            </div>
                </div>
                )}
            </div>

            <StepNavigation
                onBack={onBack}
                onNext={handleSubmit(onSubmit)}
                nextLabel="Próximo"
                hideBack={true}
            />
        </form>
        </>
    );
};

export default Step1Form;
